/**
 * TwoFactorChallenge - Login challenge UI for 2FA verification
 *
 * Shows after password verification when user has 2FA enabled.
 * Supports TOTP code entry and backup code fallback.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp';
import { Shield, Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { BackupCodeEntry } from './BackupCodeEntry';

export interface TwoFactorChallengeProps {
  /** Factor ID from the MFA challenge */
  factorId: string;
  /** Callback when verification succeeds */
  onSuccess?: () => void;
  /** Callback to go back to login */
  onBack?: () => void;
}

type ChallengeMode = 'totp' | 'backup';

export function TwoFactorChallenge({
  factorId,
  onSuccess,
  onBack,
}: TwoFactorChallengeProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<ChallengeMode>('totp');
  const [otpValue, setOtpValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otpValue.length === 6 && mode === 'totp' && !verifying) {
      handleVerifyCode(otpValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValue, mode]);

  const handleVerifyCode = useCallback(async (code: string) => {
    if (verifying) return;

    setVerifying(true);
    setError(null);

    try {
      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) {
        console.error('[TwoFactorChallenge] Challenge error:', challengeError);
        setError('Failed to initiate verification. Please try again.');
        setVerifying(false);
        return;
      }

      // Verify the code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) {
        console.error('[TwoFactorChallenge] Verify error:', verifyError);
        setError('Invalid code. Please check and try again.');
        setOtpValue('');
        setVerifying(false);
        return;
      }

      if (verifyData) {
        toast.success('Verification successful!');

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      console.error('[TwoFactorChallenge] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setVerifying(false);
    }
  }, [factorId, navigate, onSuccess, verifying]);

  const handleBackupCodeVerify = async (code: string): Promise<boolean> => {
    setVerifying(true);
    setError(null);

    try {
      // For backup codes, we need to verify through a different endpoint
      // Supabase MFA doesn't have built-in backup code support in the client SDK
      // This would typically be handled by a custom edge function
      const { data, error: verifyError } = await supabase.functions.invoke('verify-backup-code', {
        body: { code, factorId },
      });

      if (verifyError || !data?.success) {
        setError('Invalid backup code. Please try again.');
        return false;
      }

      toast.success('Backup code accepted!');

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/');
      }
      return true;
    } catch (err) {
      console.error('[TwoFactorChallenge] Backup code error:', err);
      setError('Failed to verify backup code. Please try again.');
      return false;
    } finally {
      setVerifying(false);
    }
  };

  if (mode === 'backup') {
    return (
      <BackupCodeEntry
        onVerify={handleBackupCodeVerify}
        onBack={() => setMode('totp')}
        loading={verifying}
        error={error}
      />
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex justify-center mb-2">
          <div className="p-3 rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
        <CardDescription>
          Enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* OTP Input */}
        <div className="flex flex-col items-center gap-4">
          <InputOTP
            maxLength={6}
            value={otpValue}
            onChange={setOtpValue}
            disabled={verifying}
            autoFocus
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>

          <p className="text-sm text-muted-foreground text-center">
            Open your authenticator app to view your code
          </p>
        </div>

        {/* Remember Device */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="remember-device"
            checked={rememberDevice}
            onCheckedChange={(checked) => setRememberDevice(checked === true)}
            disabled={verifying}
          />
          <Label
            htmlFor="remember-device"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            Remember this device for 30 days
          </Label>
        </div>

        {/* Verify Button */}
        <Button
          className="w-full"
          onClick={() => handleVerifyCode(otpValue)}
          disabled={otpValue.length !== 6 || verifying}
        >
          {verifying ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </Button>

        {/* Backup Code Link */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => setMode('backup')}
            className="text-sm text-primary hover:underline disabled:opacity-50"
            disabled={verifying}
          >
            Lost access to your authenticator? Use a backup code
          </button>
        </div>

        {/* Back to Login */}
        {onBack && (
          <Button
            variant="ghost"
            className="w-full"
            onClick={onBack}
            disabled={verifying}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
